/**
 * Comandi rapidi del Lead/Cliente in cima al pannello della chat (WhatsApp e
 * Instagram/Messenger): pipeline e fase, stato e assegnatario, con le stesse
 * righe di proprieta' della scheda (InlineOptionRow / UserSelectRow).
 * La fase "persa" apre il drawer che chiede il motivo, come nella scheda.
 */
import { useState } from 'react';
import { CircleDot, Flag, GitBranch, User } from 'lucide-react';
import type { Client } from '@/lib/types';
import { InlineOptionRow, UserSelectRow } from '@/components/shared/property-rows';
import LeadStatusUpdateDrawer from '@/components/leads/LeadStatusUpdateDrawer';
import { tip } from '@/components/messages/ChatTooltip';
import { useChatLeadControls } from './useChatLeadControls';

const icon = (Icon: typeof Flag) => <Icon className="h-3 w-3" strokeWidth={1.5} />;

export function ChatLeadControls({ client }: { client: Client }) {
  const c = useChatLeadControls(client);
  // Fase "persa" scelta qui: prima il motivo nel drawer, poi la scrittura della fase.
  const [lostStageId, setLostStageId] = useState<string | null>(null);

  const onStage = (stageId: string | null) => {
    if (!stageId) return;
    if (c.stages.find((s) => s.id === stageId)?.is_lost) { setLostStageId(stageId); return; }
    void c.setStage(stageId);
  };

  return (
    <div className="px-2 py-1.5 border-b border-[rgba(55,53,47,0.06)] dark:border-white/6">
      {c.pipelineId ? (
        <div className="flex items-center gap-1.5 px-1.5 pb-0.5 text-[10px] text-[#6F6E69] dark:text-white/60"
          {...tip('Pipeline in cui si trova il Lead/Cliente')}>
          <GitBranch className="h-3 w-3" strokeWidth={1.5} />
          <span className="truncate">{c.pipelineName}</span>
        </div>
      ) : (
        <div {...tip('Il Lead/Cliente non e\' in nessuna pipeline: sceglila per metterlo nella sua prima fase')}>
          <InlineOptionRow
            icon={icon(GitBranch)}
            label="Pipeline"
            value={null}
            options={c.pipelineOptions}
            onSelect={(v) => { if (v) void c.setPipeline(v); }}
            canEdit={c.canEdit && c.pipelineOptions.length > 0}
            loading={c.saving === 'pipeline'}
            placeholder="Senza pipeline"
            searchPlaceholder="Cerca pipeline..."
          />
        </div>
      )}

      <InlineOptionRow
        icon={icon(Flag)}
        label="Fase pipeline"
        value={c.currentStageId}
        options={c.stageOptions}
        onSelect={onStage}
        canEdit={c.canEdit}
        valueStyle="chip"
        loading={c.saving === 'stage'}
        placeholder={c.isLead ? 'Nuovo' : 'Vuoto'}
        searchPlaceholder="Cerca fase..."
      />

      <InlineOptionRow
        icon={icon(CircleDot)}
        label="Stato"
        value={client.stato_cliente || null}
        options={c.stateOptions}
        onSelect={(v) => { if (v) void c.setState(v); }}
        canEdit={c.canEdit}
        valueStyle="chip"
        loading={c.saving === 'state'}
        placeholder="Vuoto"
        searchPlaceholder="Cerca stato..."
      />

      <UserSelectRow
        icon={icon(User)}
        label="Assegnatario"
        value={client.assigned_to || ''}
        onChange={(v) => { void c.setAssignee(v); }}
        users={c.userOptions}
        placeholder={c.saving === 'assignee' ? 'Salvataggio...' : 'Non assegnato'}
        disabled={!c.canEdit || c.saving === 'assignee'}
      />

      {lostStageId && (
        <LeadStatusUpdateDrawer
          isOpen
          onClose={() => setLostStageId(null)}
          currentStage={lostStageId}
          leadId={client.id}
          currentAssignee={client.assigned_to || undefined}
          currentSalesRepId={client.sales_rep_id || undefined}
          leadStages={c.stages}
          leadPipelineId={c.pipelineId}
          onStageUpdated={() => setLostStageId(null)}
          onNotesUpdated={() => {
            // Con requireNote il drawer scrive nota e motivo; la fase la scrive chi lo apre
            // (stesso contratto della scheda e del kanban).
            const stageId = lostStageId;
            setLostStageId(null);
            if (stageId) void c.setStage(stageId);
          }}
          translations={{}}
          defaultTab="note"
          requireNote
          defaultStatusOpen
          priorStageId={c.currentStageId || undefined}
        />
      )}
    </div>
  );
}
